import { BinaryTreeNodeUser } from "functions/src/Users/UsersInterfaces";

// Actions
export const loadSubtree = "loadSubtree";
export const setRoot = "setRoot";
export const resetTree = "resetTree";
export const expandSubtree = "expandSubtree";
export const collapseSubtree = "collapseSubtree";
export const loadViewState = "loadViewState";
export const saveViewState = "saveViewState";
export const loadNodesBatch = "loadNodesBatch";
export const refreshNodePointsIfStale = "refreshNodePointsIfStale";

// Mutations
export const ADD_NODES = "ADD_NODES";
export const SET_ROOT = "SET_ROOT";
export const SET_HOME = "SET_HOME";
export const CLEAR = "CLEAR";
export const EXPAND_MANY = "EXPAND_MANY";
export const COLLAPSE_MANY = "COLLAPSE_MANY";
export const CLEAR_EXPANDED = "CLEAR_EXPANDED";
export const SET_FOCUS = "SET_FOCUS";
export const APPLY_VIEW_STATE = "APPLY_VIEW_STATE";

// Firestore doc id under users/{username}/userSettings/ that persists this view.
export const VIEW_STATE_DOC = "binaryTreeViewStatus";
export const VIEW_STATE_SCHEMA_VERSION = 1;

// Transitional: `binary-tree` is being abandoned for the new `binaryTree`
// (camelCase, like every other collection — issue #534). While both coexist,
// the view reads the new one through this flag; the callable maps it to the
// collection name server-side. Flip to true once `binaryTree` is seeded
// (Fase 1b). At go-live `binaryTree` becomes the sole tree and this flag + the
// old collection get removed.
export const USE_NEW_TREE = false;

export interface BinaryTreeState {
  rootUsername: string;
  // Entry-point root (search / initial load). Re-rooting and up-navigation do
  // NOT change it, so "go to root" always returns to where exploration began.
  homeUsername: string;
  nodes: Map<string, BinaryTreeNodeUser>;
  // Epoch-ms of the last time each node's OWN points were fetched, keyed by the
  // same lowercased username as `nodes`. Powers the lazy, popover-driven points
  // refresh: on open we re-fetch a node only if its stamp is older than the TTL.
  // Kept as a sidecar map — NOT a field on the node — so this client-only cache
  // metadata never leaks into the server DTO (BinaryTreeNodeUser) and the whole
  // feature stays contained in this store module.
  pointsFetchedAt: Map<string, number>;
  // Expansion state keyed by username so it survives re-root and route changes
  // (the store lives for the whole session). Kept as a plain record for rock
  // solid Vue 3 reactivity on add/delete.
  expanded: Record<string, boolean>;
  // Usernames that were centered in the viewport, kept here so scroll position
  // restores on same-session route changes too (not just on fresh re-login).
  focusUsernames: string[];
  // Bumped on every view-state change (expand/collapse/re-root) so the view can
  // cheaply watch one primitive to know when to persist.
  revision: number;
}

export interface LoadSubtreeRequest {
  username: string;
  levels: number;
}

// Payload for the lazy points refresh. The view dispatches a bare username
// string (default TTL); the object form lets callers override the staleness
// window when needed.
export interface RefreshNodePointsRequest {
  username: string;
  ttlMs?: number;
}

export interface ExpandSubtreeRequest {
  username: string;
  levels: number;
}

// Persisted per-user view state. Only structure is stored — every volatile field
// (remaining days, active/VIP/lite, points, rank, children…) is recomputed from
// getBinaryTree on restore. See documentation/USER_SETTINGS.md.
export interface BinaryTreeViewStatus {
  _usernamePK: string; // owner, lowercase (queryable, matches userFlags)
  homeUsername: string; // origin root ("go to root" target)
  anchorUsername: string; // viewport root that is rendered
  openNodes: string[]; // expanded usernames
  expandDepth: number; // "Niveles/clic" setting
  focusUsernames?: string[]; // nodes that were centered, to restore scroll near them
  focusPath?: string[]; // anchor→focus ancestor column, always kept on capped restore
  savedAt: number; // epoch ms (for staleness / future temporal display)
  schemaVersion: number;
}

export interface SaveViewStateRequest {
  username: string;
  expandDepth: number;
  focusUsernames: string[];
}

export interface ApplyViewStatePayload {
  homeUsername: string;
  anchorUsername: string;
  openNodes: string[];
  focusUsernames: string[];
}
